import { format } from "date-fns";
import fileDownload from "js-file-download";
import JSZip from "jszip";
import { useEffect, useRef, useState } from "react";
import { DataGrid, Icon, IconButton, Tooltip } from "../../../../../components";
import { filtrosUrl, responseStreamHelper } from "../../../../../helpers";
import {
  AdzapService,
  BoletoService,
  MeioPagamentoService,
  notification,
  PessoaService,
  PlugBoletoService,
  SessionService,
} from "../../../../../services";
import { formatDate, formatPrice, responseErros } from "../../../../../utils";
import { boletosHelper } from "../../../../../helpers/boletos.helper";
import {
  boletoSituacoes,
  statusValidosConsulta,
} from "../../../resources/constantes";
import ButtonMenuBoleto from "./button-menu-boleto.component";
import CodigoRemessaErrorDialog from "./erro-envio-remessa.dialog";
import FormFiltroAvancadoBoleto from "./form-filtro-avancado-boleto.component";
import MenuActions from "./menu-actions.component";
import MinimalCustomLayout from "./minimal-custom.layout";
import EnvioWhatsappDialog from "./envio-whatsapp.dialog";

const ListBoletosView = () => {
  const fileInputRef = useRef(null);

  const [boletos, setBoletos] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [meiosPagamentoList, setMeiosPagamentoList] = useState([]);
  const [boletoSelecionado, setBoletoSelecionado] = useState(null);
  const [boletosSelecionados, setBoletosSelecionados] = useState([]);
  const [openDialogErroRemessa, setOpenDialogErroRemessa] = useState(false);
  const [erroIds, setErroIds] = useState([]);
  const [anchorMenu, setAnchorMenu] = useState(null);
  const [openWhatsappDialog, setOpenWhatsappDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const boletoService = new BoletoService();
  const pessoaService = new PessoaService();
  const meioPagamentoService = new MeioPagamentoService();
  const plugBoletoService = new PlugBoletoService();
  const adzapService = new AdzapService();
  const sessionService = new SessionService();
  const dadosEmpresa = sessionService.getEmpresaAndUsuario();

  const colunas = [
    {
      field: "status",
      headerName: "Status",
      flex: 50,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const statusTemp = boletoSituacoes.find(
          (i) => i.nome === params.value,
        ) ?? {
          label: "Desconhecido",
          cor: "#BFBFBF",
        };
        return (
          <Tooltip title={statusTemp.label} arrow placement="right">
            <Icon>
              <i
                className="ph-fill ph-circle"
                style={{ color: statusTemp.cor, fontSize: 18 }}
              ></i>
            </Icon>
          </Tooltip>
        );
      },
    },
    {
      field: "tituloDocumento",
      headerName: "Título",
      align: "center",
      headerAlign: "center",
      flex: 50,
      filtrar: true,
    },
    {
      field: "integradorId",
      headerName: "Integrador",
      flex: 100,
      sortable: false,
    },
    {
      field: "clienteId",
      headerName: "Cliente",
      flex: 200,
      valueGetter: (params) => params.row.cliente?.nomeRazaoSocial ?? "",
    },
    {
      field: "meioPagamentoId",
      headerName: "Meio de Pagamento",
      flex: 150,
      valueGetter: (params) =>
        meiosPagamentoList.find((meio) => meio.id === params.value)
          ?.descricao ?? "",
    },
    {
      field: "dataVencimento",
      headerName: "Data Vencimento",
      align: "center",
      headerAlign: "center",
      flex: 100,
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "valorTotal",
      headerName: "Valor Total",
      align: "right",
      headerAlign: "right",
      flex: 100,
      valueGetter: (params) => formatPrice(params.value || ""),
    },
    {
      field: "integradorImpressaoId",
      headerName: "Ações",
      align: "center",
      headerAlign: "center",
      sortable: false,
      flex: 100,
      disableClickEventBubbling: true,
      renderCell: (params) =>
        statusValidosConsulta.includes(params.row.status) ? (
          <div>
            <IconButton
              onClick={async (event) => {
                event.stopPropagation();
                setLoading(true);
                await boletosHelper.imprimirBoleto(params.value, dadosEmpresa);
                setLoading(false);
              }}
            >
              <Tooltip title="Imprimir Boleto" arrow placement="top">
                <i className="ph-fill ph-printer" style={{ fontSize: 18 }}></i>
              </Tooltip>
            </IconButton>
            <IconButton
              onClick={async (event) => {
                event.stopPropagation();
                setBoletoSelecionado(params.row);
                setOpenWhatsappDialog(true);
              }}
            >
              <Tooltip title="Enviar via Whatsapp" arrow placement="top">
                <i
                  className="ph-bold ph-whatsapp-logo"
                  style={{ fontSize: 18, color: "#25D366" }}
                ></i>
              </Tooltip>
            </IconButton>
            <IconButton
              onClick={async (event) => {
                event.stopPropagation();
                setAnchorMenu(event.currentTarget);
                setBoletoSelecionado(params.row);
              }}
            >
              <Tooltip title="Mostrar mais ações" arrow placement="top">
                <i
                  className="ph-fill ph-dots-three-outline"
                  style={{ fontSize: 18 }}
                ></i>
              </Tooltip>
            </IconButton>
          </div>
        ) : (
          <Tooltip title="Boleto inválido para consulta" arrow placement="top">
            <i
              className="ph-bold ph-warning-circle"
              style={{ fontSize: 22, color: "#EA9D2B" }}
            ></i>
          </Tooltip>
        ),
    },
  ];

  async function imprimirBoletosLote() {
    setLoading(true);
    const boletosFiltrados = boletos.filter((item) =>
      boletosSelecionados.includes(item.id),
    );
    try {
      let zip = new JSZip();

      for (const item of boletosFiltrados) {
        const result = await plugBoletoService.imprimirBoleto(
          item.integradorImpressaoId,
          dadosEmpresa.empresa.cnpjCpf,
        );
        if (!result.ok) {
          responseErros(result);
          throw new Error("Erro ao buscar dados de impressão do boleto");
        }
        const blob = await responseStreamHelper.getBlobPdf(result);
        const cliente = item.cliente;
        zip.file(
          `boleto-${cliente.nomeRazaoSocial?.replace(
            /\s+/g,
            "_",
          )}-${item.tituloDocumento?.replace(/\s+/g, "")}.pdf`,
          blob,
        );
      }

      zip.generateAsync({ type: "blob" }).then((content) => {
        const date = new Date();
        fileDownload(
          content,
          `boletos-lote-${format(date, "ddMMyyyyHHmmss")}.zip`,
        );
        setLoading(false);
      });
    } catch (error) {
      setLoading(false);
      notification.erroInternoServidor();
    }
  }

  async function enviarMensagensLote() {
    setLoading(true);

    const boletosAEnviar = boletos.filter(({ id }) =>
      boletosSelecionados.includes(id),
    );
    try {
      for (let i = 0, lote = 1; i < boletosAEnviar.length; i += 10, lote++) {
        const boletosLote = boletosAEnviar.slice(i, i + 10);
        const boletosErro = boletosLote.filter(
          (item) => !item.cliente?.celular,
        );
        if (boletosErro.length) {
          notification.alertaGenericos(
            `O cliente ${
              boletosErro[0].cliente?.nomeRazaoSocial ?? ""
            } não possui número de celular cadastrado`,
          );
          setLoading(false);
          return;
        }
        const messages = [];
        for (const boleto of boletosLote) {
          const mensagem = await boletosHelper.formatarMensagemAdzap(
            boleto,
            boleto.cliente?.celular,
            dadosEmpresa,
          );
          messages.push(mensagem);
        }

        const result = await adzapService.enviarMensagemLote({
          info: `Boletos - Lote ${lote}`,
          messages,
          scheduledFor: 1,
        });

        if (result.isAxiosError) {
          responseErros(result);
          setLoading(false);
          return;
        }
      }
    } catch (error) {
      notification.erroGenericos(error?.message ?? "Erro interno de servidor");
      return;
    }

    notification.sucessoGenericos("Mensagens enviadas com sucesso");
    setLoading(false);
  }

  async function getBase64ToBlob(base64) {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: "text/plain" });
  }

  async function gerarRemessa() {
    setLoading(true);

    const boletosAEnviar = boletos
      .filter(({ id }) => boletosSelecionados.includes(id))
      .map(({ integradorId }) => integradorId);
    const result = await plugBoletoService.gerarRemessa(
      boletosAEnviar,
      dadosEmpresa.empresa.cnpjCpf,
    );
    if (result?._dados?._sucesso?.length) {
      let zip = new JSZip();
      for (const remessa of result._dados._sucesso) {
        const blob = await getBase64ToBlob(remessa.remessa);
        zip.file(remessa.arquivo, blob);
      }

      zip.generateAsync({ type: "blob" }).then((content) => {
        const date = new Date();
        fileDownload(
          content,
          `remessa-lote-${format(date, "ddMMyyyyHHmmss")}.zip`,
        );
        setLoading(false);
      });
    }

    if (result._dados?._falha?.length) {
      const ids = result._dados._falha.map((item) => item.idintegracao);
      setErroIds(ids);
      setOpenDialogErroRemessa(true);
    } else {
      notification.sucessoGenericos("Download realizado com sucesso");
    }
    setLoading(false);
  }

  async function descarteEmLote(boletosIds) {
    setLoading(true);

    const boletosAEnviar = boletos
      .filter(({ id }) => boletosIds.includes(id))
      .map(({ integradorId }) => integradorId);
    const result = await plugBoletoService.descartarEmLote(
      boletosAEnviar,
      dadosEmpresa.empresa.cnpjCpf,
    );
    if (result?._dados?._sucesso?.length) {
      const idsDescartados = result._dados._sucesso.map(
        ({ idintegracao }) => idintegracao,
      );
      const updateBody = boletos
        .filter(({ integradorId }) => idsDescartados.includes(integradorId))
        .map((item) => ({ id: item.id, status: "DESCARTADO" }));
      const resultDelete = await boletoService.update(updateBody);
      if (!resultDelete.isAxiosError) {
        notification.sucessoGenericos("Boleto(s) descartado(s) com sucesso");
        await buscarBoletos();
      } else {
        responseErros(resultDelete);
      }
    } else {
      notification.alertaGenericos("Erro ao descartar boleto(s)");
    }

    setLoading(false);
  }

  async function baixarEmLote(boletosIds) {
    setLoading(true);

    const boletosAEnviar = boletos
      .filter(({ id }) => boletosIds.includes(id))
      .map(({ integradorId }) => integradorId);
    const result = await plugBoletoService.baixarEmLote(
      boletosAEnviar,
      dadosEmpresa.empresa.cnpjCpf,
    );
    if (result?._dados?.situacao) {
      notification.sucessoGenericos(
        "Boleto(s) encaminhado(s) para processamento de baixa",
      );
    } else {
      notification.alertaGenericos("Erro ao processar baixa");
    }

    setLoading(false);
  }

  async function consultarSituacao() {
    setLoading(true);

    const boletosAEnviar = boletos.filter(({ id }) =>
      boletosSelecionados.includes(id),
    );
    const params = `idintegracao=${boletosAEnviar
      .map(({ integradorId }) => integradorId)
      .join(",")}`;
    const result = await plugBoletoService.consultarSituacao(
      params,
      dadosEmpresa.empresa.cnpjCpf,
    );
    if (!result.ok) {
      responseErros(result);
      setLoading(false);
      notification.alertaGenericos("Erro ao consultar boletos");
    }
    const response = await result.json();
    const boletosApi = response._dados;
    if (boletosApi?.length) {
      const updateBody = [];
      for (const boleto of boletosApi) {
        const boletoOriginal = boletosAEnviar.find(
          ({ integradorId }) => boleto.IdIntegracao === integradorId,
        );
        if (boletoOriginal.status !== boleto.situacao) {
          updateBody.push({
            id: boletoOriginal.id,
            status: boleto.situacao,
          });
        }
      }

      if (updateBody.length) {
        const res = await boletoService.update(updateBody);
        if (!res.isAxiosError) {
          notification.sucessoGenericos("Boletos atualizados com sucesso");
        } else {
          responseErros(result);
        }

        buscarBoletos();
      } else {
        notification.sucessoGenericos(
          "Não houve nenhuma alteração nos boletos selecionados",
        );
      }
    }
    setLoading(false);
  }

  const adicionarArquivoRetorno = () => {
    fileInputRef.current.click();
  };

  async function processarRetorno(event) {
    setLoading(true);

    const file = event.target.files[0];
    if (!file) return;

    const base64Data = await boletosHelper.blobToBase64(file);
    if (base64Data) {
      const result = await plugBoletoService.processarRetorno(
        { arquivo: base64Data },
        dadosEmpresa.empresa.cnpjCpf,
      );
      if (result) {
        if (result._status === "sucesso") {
          notification.sucessoGenericos(result._mensagem);
        } else {
          notification.erroGenericos(result._mensagem);
        }
      } else {
        notification.erroInternoServidor();
      }
    } else {
      notification.erroGenericos("Erro ao ler arquivo de retorno");
    }

    setLoading(false);
  }

  useEffect(() => {
    const filtros = filtrosUrl.get();
    buscarBoletos(filtros);
  }, []);

  const buscarClientes = (listaIds, boletos) => {
    if (listaIds.length > 0) {
      const filtro = {
        nonPaginated: true,
        tiposPessoaId: 1,
        id: listaIds,
      };
      pessoaService.getAll(filtro).then((result) => {
        if (!result.isAxiosError) {
          const boletosTemp = boletos.map((item) => {
            const cliente = result.data?.find(
              ({ id }) => item.clienteId === id,
            );
            return {
              ...item,
              cliente,
            };
          });
          setBoletos(boletosTemp);
        } else {
          responseErros(result);
        }
      });
    } else {
      setBoletos([]);
    }
  };

  const buscarMeiosPagamentos = (listaIds) => {
    if (listaIds.length > 0) {
      const filtro = {
        id: listaIds,
        nonPaginated: true,
      };
      meioPagamentoService.getAll(filtro).then((result) => {
        if (!result.isAxiosError) {
          setMeiosPagamentoList(result.data);
        } else {
          responseErros(result);
        }
      });
    }
  };

  const buscarRelacionamentos = (boletos) => {
    const clienteIds = new Set(),
      meioPagamentoIds = new Set();
    boletos.forEach((item) => {
      if (item.clienteId) clienteIds.add(item.clienteId);
      if (item.meioPagamentoId) meioPagamentoIds.add(item.meioPagamentoId);
    });
    buscarClientes([...clienteIds], boletos);
    buscarMeiosPagamentos([...meioPagamentoIds]);
  };

  const buscarBoletos = async function (filter) {
    setLoading(true);
    const result = await boletoService.getAll(filter);
    if (!result.isAxiosError) {
      const listaBoletos = result.data.rows ?? [];
      setRowCount(result.data.count);
      buscarRelacionamentos(listaBoletos);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarPesquisaAvancada = async (filter) => {
    setLoading(true);
    const porIntervalo = [];
    const filtrosAvancadosTemp = {
      ...filter,
    };
    if (
      filtrosAvancadosTemp.dataVencimentoStart &&
      filtrosAvancadosTemp.dataVencimentoEnd
    ) {
      porIntervalo.push({
        coluna: "dataVencimento",
        de: filtrosAvancadosTemp.dataVencimentoStart,
        ate: filtrosAvancadosTemp.dataVencimentoEnd,
      });
    }
    delete filtrosAvancadosTemp.dataVencimentoStart;
    delete filtrosAvancadosTemp.dataVencimentoEnd;

    const result = await boletoService.getAllFiltroAvancado({
      ...filtrosAvancadosTemp,
      restritiva: true,
      porIntervalo,
    });
    if (!result.isAxiosError) {
      const listaBoletos = result.data.rows ?? [];
      setRowCount(result.data.count);
      buscarRelacionamentos(listaBoletos);

      filtrosUrl.set(filtrosAvancadosTemp);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const sendServerDatagrid = (props) => {
    const { filtros, tipoFiltro } = props;
    if (tipoFiltro === "simples") {
      buscarBoletos(filtros);
    } else {
      buscarPesquisaAvancada(filtros);
    }
  };

  return (
    <MinimalCustomLayout
      title="Boletos"
      loading={loading}
      ButtonMenu={
        <ButtonMenuBoleto
          boletos={boletos}
          boletosSelecionados={boletosSelecionados}
          consultarSituacao={consultarSituacao}
          imprimirBoletosLote={imprimirBoletosLote}
          enviarMensagensLote={enviarMensagensLote}
          gerarRemessa={gerarRemessa}
          descarteEmLote={descarteEmLote}
          baixarEmLote={baixarEmLote}
          adicionarArquivoRetorno={adicionarArquivoRetorno}
        />
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".ret,.txt"
        onChange={processarRetorno}
        style={{ display: "none" }}
      />
      <DataGrid
        rows={boletos}
        rowCount={rowCount}
        columns={colunas}
        listaStatus={boletoSituacoes}
        disableAcoes
        checkboxSelection
        selectionModel={boletosSelecionados}
        onRowSelectionModelChange={setBoletosSelecionados}
        paginationMode={"server"}
        sendServer={(props) => sendServerDatagrid(props)}
        FormFiltroAvancado={FormFiltroAvancadoBoleto}
      />
      <CodigoRemessaErrorDialog
        open={openDialogErroRemessa}
        setOpen={setOpenDialogErroRemessa}
        erroIds={erroIds}
      />
      <MenuActions
        anchorEl={anchorMenu}
        setAnchorEl={setAnchorMenu}
        dadosEmpresa={dadosEmpresa}
        boletoSelecionado={boletoSelecionado}
        buscarBoletos={buscarBoletos}
        setLoading={setLoading}
      />
      <EnvioWhatsappDialog
        open={openWhatsappDialog}
        setOpen={setOpenWhatsappDialog}
        boleto={boletoSelecionado}
        dadosEmpresa={dadosEmpresa}
      />
    </MinimalCustomLayout>
  );
};

export default ListBoletosView;
